import { AuthenticationRequest } from '@feathersjs/authentication'
import useStores, { type Stores } from '~/composables/useStores'
import { shiftWindowQuery } from '@restify/packages/helpers/shift-window'
import useFeathers from '~/composables/useFeathers'
import { useAppStore } from '~/stores/app'
import { useNotification } from '@restify/packages/design-system/low-level/AppNotification/useNotification'
import { parse as HelpersJwtParse } from '@restify/packages/helpers/jwt'
import { defineStore } from 'pinia'

const { notifyWarning } = useNotification()

const setPreferredEmails = (user: Stores['users']['Result']) => {
  let preferredEmails: Stores['users']['Result'][] = []

  try {
    preferredEmails = JSON.parse(
      window.localStorage.getItem('preferredEmails') || '[]',
    )
  } catch (error) {
    console.log(error)
  }

  if (preferredEmails.findIndex((item) => item.email === user.email) < 0) {
    preferredEmails.push(user)
  }

  window.localStorage.setItem(
    'preferredEmails',
    JSON.stringify(preferredEmails),
  )
}

export const useAuthStore = defineStore('auth', {
  state: (): {
    accessToken: null | string
    user: null | Stores['users']['Result']
    authentication: unknown
    preferredEmail: string | null
    preferredAppId: string | null
    preferredStorefrontId: string | null
    lastSessionLogin: null | number
    lastActivity: number
    isLoading: boolean
  } => ({
    accessToken: null,
    user: null,
    authentication: {},
    preferredEmail: null,
    preferredAppId: null,
    preferredStorefrontId:
      window.localStorage.getItem('preferredStorefrontId') || null,
    lastSessionLogin: null,
    lastActivity: Date.now(),
    isLoading: false,
  }),
  getters: {
    isUserAuthenticated(): boolean {
      return !!this.accessToken
    },
    reactiveUser(): Stores['users']['Result'] | null {
      const { users: UsersStore } = useStores()

      return this.user ? UsersStore.itemsById[this.user._id].value : null
    },
    userApp(): Stores['apps']['Result'] | null {
      const { apps: AppsStore } = useStores()
      const appId = this.preferredAppId || this.reactiveUser?.appId

      if (!appId) return null

      return AppsStore.itemsById[appId].value || null
    },
    userPermittedStorefrontId(): string | undefined {
      if (!this.reactiveUser) return undefined

      if (this.reactiveUser?.permissions?.storefrontIds?.[0]) {
        return this.reactiveUser.permissions.storefrontIds[0]
      }

      const { storefronts: StorefrontsStore } = useStores()

      const storefronts = StorefrontsStore.findInStore({
        appId: this.reactiveUser.appId,
        $sort: { createdAt: -1 },
      })

      return storefronts[0].value?._id
    },
  },
  actions: {
    async authenticate(authData: AuthenticationRequest) {
      const { apps: AppsStore } = useStores()

      const response = await this.authenticateBase({
        ...authData,
        ...(this.preferredAppId ? { preferredAppId: this.preferredAppId } : {}),
        ...(this.preferredStorefrontId
          ? { preferredStorefrontId: this.preferredStorefrontId }
          : {}),
        ...(authData.preferredAppId
          ? { preferredAppId: authData.preferredAppId }
          : {}),
        ...(authData.preferredStorefrontId
          ? { preferredStorefrontId: authData.preferredStorefrontId }
          : {}),
      })

      const parsedToken = response?.accessToken
        ? HelpersJwtParse(response.accessToken)
        : null

      // Load the permitted app and select the storefront BEFORE revealing the
      // session, so screens mount with their data already in place.
      if (parsedToken?.appId) {
        try {
          await AppsStore.get(parsedToken.appId)

          this.setPreferredStorefrontId(parsedToken.storefrontId)

          if (parsedToken.isSuperadmin) {
            this.setPreferredAppId(parsedToken.appId)
          } else {
            this.removePreferredAppId()
          }
        } catch (error) {
          notifyWarning('Error happened', (error as Error).message)

          this.logout()

          throw error
        }
      }

      // Everything is ready → flip isUserAuthenticated, which reveals the shell.
      this.accessToken = response.accessToken

      return response
    },
    async authenticateBase(authData: AuthenticationRequest) {
      const {
        users: UsersStore,
        storefronts: StorefrontsStore,
        shifts: ShiftsStore,
      } = useStores()
      const feathersClient = useFeathers()

      try {
        this.$state.isLoading = true

        const response = await feathersClient
          .authenticate(authData)
          .catch((error) => {
            throw error
          })

        const time = new Date().getTime()

        await Promise.all([
          UsersStore.get(response.user._id),
          ShiftsStore.find({
            query: {
              ...shiftWindowQuery(time),
              $sort: { startsAt: 1 },
            },
          }),
          StorefrontsStore.find({
            query: {
              appId: response.user.appId,
              $sort: { createdAt: -1 },
            },
          }),
        ])

        this.$state.authentication = response.authentication
        this.$state.user = { ...response.user }
        this.$state.isLoading = false
        this.$state.lastSessionLogin = Date.now()

        setPreferredEmails(response.user)

        return response
      } catch (error) {
        this.logout()

        throw error
      }
    },
    async logout() {
      const feathersClient = useFeathers()

      this.$state.isLoading = true

      window.localStorage.removeItem('preferredAppId')
      this.removePreferredAppId()

      return (
        feathersClient.authentication.removeAccessToken() &&
        feathersClient.authentication
          .reset()
          .then(() => {
            this.$state.accessToken = null
            this.$state.authentication = {}
            this.$state.user = null
            this.$state.lastSessionLogin = null
            this.$state.lastActivity = Date.now()
            this.$state.isLoading = false
          })
          .catch((error) => notifyWarning('Error happened', error.message))
      )
    },
    setPreferredAppId(id: string | null) {
      if (!this.reactiveUser?.isSuperadmin || !id) return

      this.preferredAppId = id
      window.localStorage.setItem('preferredAppId', id)
    },
    setPreferredStorefrontId(id: string | null) {
      this.preferredStorefrontId = id

      if (id) window.localStorage.setItem('preferredStorefrontId', id)

      useAppStore().setSelectedStorefrontId(id)
    },
    removePreferredAppId() {
      this.preferredAppId = null
      window.localStorage.removeItem('preferredAppId')
    },
  },
})
